/**
 * MeusChamadosPage — chamados ABERTOS pelo próprio usuário (B1.6 §6).
 *
 * FASE-C: cabeçalho canônico sempre presente (o h1 sumia durante o carregamento),
 * ação primária no header, erro humano e empty state que convida em vez de
 * constatar. A lista virou `ListaChamados` com anatomia fixa — número em SF Mono
 * (é um identificador), título com espaço pra 2 linhas no 375px e badge de
 * situação alinhado à direita sem apertar o texto.
 *
 * NÃO REVERTER:
 *   - Backend filtra criadoPor = user.id (server-side, fonte da verdade)
 *   - Link pra abrir novo chamado só com permissão `abrir_chamado`
 *   - Lista ordenada por mais recente (data DESC)
 */

import { Link } from 'react-router-dom';

import { Alert, Badge, Button, EmptyState } from '@/components/ui';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import {
  faseLabel,
  faseBadgeVariant,
  useClienteDashboard,
  useMeusChamados,
  type ChamadoListItem,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

import { formatarData } from '@/lib/format-date';
export function MeusChamadosPage() {
  const { clienteAtivo } = useCliente();
  const dashQuery = useClienteDashboard();
  const { data, isLoading, isError, error } = useMeusChamados();

  const podeAbrirChamado = dashQuery.data?.permissoes.abrir_chamado ?? false;

  return (
    <PortalPage
      testId="meus-chamados-page"
      title="Meus chamados"
      subtitle={
        clienteAtivo
          ? `Os chamados que você abriu em ${clienteAtivo.nome}.`
          : 'Os chamados que você abriu.'
      }
      actions={
        podeAbrirChamado ? (
          <Link to="/cliente/chamado/novo" className="w-full sm:w-auto">
            <Button variant="primary" fullWidth data-testid="meus-novo-chamado">
              Abrir chamado
            </Button>
          </Link>
        ) : undefined
      }
    >
      {!clienteAtivo ? null : isLoading ? (
        <PortalCarregando rotulo="Carregando seus chamados…" />
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar seus chamados agora. Tente atualizar a página.')}
        </Alert>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          icon={<IconeChamado />}
          title="Nenhum chamado por aqui ainda"
          description={
            podeAbrirChamado
              ? 'Quando algo não estiver funcionando — câmera, portão, alarme —, abra um chamado e a equipe da IGM é avisada na hora.'
              : 'Você ainda não abriu nenhum chamado neste condomínio.'
          }
          action={
            podeAbrirChamado ? (
              <Link to="/cliente/chamado/novo">
                <Button variant="primary">Abrir meu primeiro chamado</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <ListaChamados items={data.items} />
      )}
    </PortalPage>
  );
}

export function ListaChamados({ items }: { items: ChamadoListItem[] }) {
  return (
    <ul className="space-y-2.5" data-testid="lista-chamados">
      {items.map((c) => (
        <li key={c.id}>
          <Link
            to={`/cliente/chamado/${c.id}`}
            className="group flex items-start gap-3 rounded-xl border border-line bg-paper-surface p-4 transition hover:border-red/40 hover:shadow-card-hover dark:border-line-dark dark:bg-paper-dark-surface"
            data-testid={`chamado-item-${c.id}`}
          >
            <div className="min-w-0 flex-1">
              {c.numeroHumano != null && (
                <span className="mb-0.5 block font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">
                  #{c.numeroHumano}
                </span>
              )}
              <span className="block font-medium text-ink-1 group-hover:text-red-deep dark:text-ink-dark-1 dark:group-hover:text-red-glow">
                {c.titulo}
              </span>
              <span className="mt-1 block text-xs text-ink-3 dark:text-ink-dark-3">
                Aberto em {formatIsoShort(c.criadoEmIso)}
              </span>
            </div>
            <FaseBadge fase={c.fase} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function FaseBadge({ fase }: { fase: string }) {
  return (
    <Badge variant={faseBadgeVariant(fase)} size="sm" className="mt-0.5 shrink-0">
      {faseLabel(fase)}
    </Badge>
  );
}

function IconeChamado() {
  // Lucide `wrench`.
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.7 6.3a4 4 0 0 0 5 5l-9 9a2.8 2.8 0 0 1-4-4l9-9a4 4 0 0 0-1 -1Z" />
    </svg>
  );
}

function formatIsoShort(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
